import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { LanguagePicker } from "@/components/shared/LanguagePicker";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Save, Loader2, Copy, Check, Edit2, Users, MessageSquare, ClipboardList, Camera } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import UserAvatar from "@/components/shared/UserAvatar";
import { Badge } from "@/components/ui/badge";
import LevelBadge from "@/components/level/LevelBadge";
import SelfAssessmentModal from "@/components/level/SelfAssessmentModal";
import RankingList from "@/components/level/RankingList";
import ResponsiveLayout from "@/components/layout/ResponsiveLayout";
import ProfileSidebar from "@/components/profile/ProfileSidebar";
import FriendsList from "@/components/social/FriendsList";
import PlayerStatsSection from "@/components/stats/PlayerStatsSection";
import MatchHistorySection from "@/components/stats/MatchHistorySection";
import ReportResultModal from "@/components/match/ReportResultModal";
import MembershipSection from "@/components/membership/MembershipSection";
import PaymentHistory from "@/components/booking/PaymentHistory";
import SavedCardsSection from "@/components/profile/SavedCardsSection";
import PlayerCoinsCard from "@/components/rewards/PlayerCoinsCard";
import RewardShop from "@/components/rewards/RewardShop";
import PlayerCreditsSection from "@/components/credits/PlayerCreditsSection";
import ParentChildrenSection from "@/components/junior/ParentChildrenSection";
import ChangePasswordSection from "@/components/profile/ChangePasswordSection";
import JuniorBadge from "@/components/junior/JuniorBadge";
import { isJunior } from "@/lib/juniorUtils";
import ReferralSection from "@/components/referral/ReferralSection";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import type { Tables } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

export default function Profile() {
  const { t } = useTranslation('profile');
  const { profile, signOut, user, refetchProfile } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredHand, setPreferredHand] = useState("");
  const [playerType, setPlayerType] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [showReportResult, setShowReportResult] = useState(false);

  const [icalToken, setIcalToken] = useState<string | null>(null);
  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("ical_token").eq("id", user.id).single().then(({ data }) => {
      setIcalToken(data?.ical_token ?? null);
    });
  }, [user]);

  const icalUrl = icalToken
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ical-feed?token=${icalToken}`
    : null;

  const copyIcalUrl = () => {
    if (!icalUrl) return;
    navigator.clipboard.writeText(icalUrl);
    setCopied(true);
    toast.success(t('icalCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSignOut = async () => {
    setLoggingOut(true);
    // UPPDRAG-30: empty catch — sign-out errors are best-effort (we redirect
    // regardless). The underscore-prefix marks the intentionally-ignored err.
    try { await signOut(); } catch (_e) { /* swallow — redirect happens unconditionally */ }
    window.location.href = "/auth";
  };

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setPhone(profile.phone || "");
      setPreferredHand(profile.preferred_hand || "");
      setPlayerType(profile.player_type || "");
      setBirthDate(profile.birth_date || "");
      setBio((profile as any).bio || "");
      setAvatarUrl(profile.avatar_url || null);
    }
  }, [profile]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('avatar.tooLarge')); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error(t('avatar.wrongType')); return; }
    setUploadingAvatar(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${user.id}/avatar.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = `${publicUrl}?t=${Date.now()}`;
      await supabase.from("profiles").update({ avatar_url: url }).eq("id", user.id);
      setAvatarUrl(url);
      toast.success(t('avatar.uploaded'));
      refetchProfile();
    } catch (err: any) {
      toast.error(t('avatar.uploadFailed') + ": " + err.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName, phone, preferred_hand: preferredHand || null, player_type: playerType || null, birth_date: birthDate || null, bio: bio || null } as any)
      .eq("id", user.id);
    if (error) toast.error(t('couldNotSave') + ": " + error.message);
    else { toast.success(t('profileSaved')); refetchProfile(); }
    setSaving(false);
  };

  const updatePrivacy = async (fields: Partial<Pick<Tables<"profiles">, "profile_visibility" | "show_level" | "show_match_history">>) => {
    if (!user) return;
    const { error } = await supabase.from("profiles").update(fields).eq("id", user.id);
    if (error) {
      toast.error(t('couldNotSave') + ": " + error.message);
      console.error("Privacy update error:", error);
    } else {
      toast.success(t('settingSaved'));
      refetchProfile(); // Update profile in auth context
    }
  };

  const currentLevel = profile?.level || 1;

  const profileHeader = (
    <div className="flex flex-col items-center gap-3 lg:items-start">
      <div className="relative">
        <UserAvatar avatarUrl={avatarUrl} fullName={profile?.full_name} size="xl" className="shadow-lg" />
        <label className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-primary flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors shadow-md">
          {uploadingAvatar ? <Loader2 className="h-3.5 w-3.5 text-primary-foreground animate-spin" /> : <Camera className="h-3.5 w-3.5 text-primary-foreground" />}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarUpload} disabled={uploadingAvatar} />
        </label>
      </div>
      <h1 className="text-2xl font-bold">{profile?.full_name || t('title')}</h1>
      <div className="flex items-center gap-3 flex-wrap">
        <LevelBadge level={currentLevel} size="lg" showLabel />
        {isJunior(birthDate) && <JuniorBadge />}
        {profile?.ambassador_tier && (
          <Badge variant="outline" className="gap-1">
            {profile.ambassador_tier === "legend" ? "Legend" :
             profile.ambassador_tier === "ambassador" ? "Ambassador" : "Friend"}
          </Badge>
        )}
        {!(profile as any)?.level_assessed_at && (
          <Button variant="ghost" size="icon" onClick={() => setShowAssessment(true)} className="h-8 w-8">
            <Edit2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {(profile as any)?.level_assessed_at && (
        <p className="text-xs text-muted-foreground">{t('levelLockedHelper')}</p>
      )}
      <p className="text-sm text-muted-foreground hidden lg:block">{user?.email}</p>
    </div>
  );

  const tabsContent = (
    <Tabs defaultValue="profile">
      <TabsList className="w-full overflow-x-auto flex-nowrap scrollbar-hide justify-start">
        <TabsTrigger value="profile" className="shrink-0 text-xs">{t('title')}</TabsTrigger>
        <TabsTrigger value="membership" className="shrink-0 text-xs">{t('membership')}</TabsTrigger>
        <TabsTrigger value="payments" className="shrink-0 text-xs">{t('payments')}</TabsTrigger>
        <TabsTrigger value="rewards" className="shrink-0 text-xs">{t('rewards')}</TabsTrigger>
        <TabsTrigger value="stats" className="shrink-0 text-xs">{t('statistics')}</TabsTrigger>
        <TabsTrigger value="history" className="shrink-0 text-xs">{t('myMatches')}</TabsTrigger>
        <TabsTrigger value="friends" className="shrink-0 text-xs">{t('friends')}</TabsTrigger>
        <TabsTrigger value="ranking" className="shrink-0 text-xs">{t('ranking')}</TabsTrigger>
        {isEnabled("referral_program") && <TabsTrigger value="referral" className="shrink-0 text-xs">{t('refer')}</TabsTrigger>}
      </TabsList>

      <TabsContent value="rewards" className="mt-3 space-y-4">
        <PlayerCoinsCard />
        <RewardShop />
      </TabsContent>

      <TabsContent value="profile" className="space-y-6 mt-3">
        <Card>
          <CardHeader><CardTitle className="text-lg">{t('profileInfo')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>{t('email')}</Label><Input value={user?.email || ""} disabled /></div>
            <div className="space-y-2"><Label htmlFor="name">{t('name')}</Label><Input id="name" value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
            <div className="space-y-2"><Label htmlFor="phone">{t('phone')}</Label><Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07X-XXX XX XX" /></div>
            <div className="space-y-2">
              <Label>{t('preferredHand')}</Label>
              <Select value={preferredHand} onValueChange={setPreferredHand}>
                <SelectTrigger><SelectValue placeholder={t('choose')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="right">{t('right')}</SelectItem>
                  <SelectItem value="left">{t('left')}</SelectItem>
                  <SelectItem value="both">{t('both')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('playerType')}</Label>
              <Select value={playerType} onValueChange={setPlayerType}>
                <SelectTrigger><SelectValue placeholder={t('choose')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="offensive">{t('offensive')}</SelectItem>
                  <SelectItem value="defensive">{t('defensive')}</SelectItem>
                  <SelectItem value="allround">{t('allround')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('birthDate')}</Label>
              <div className="relative">
                <Input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className={cn(!birthDate && "text-transparent")}
                />
                {!birthDate && (
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {t('birthDatePlaceholder')}
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('bio.label')}</Label>
              <Textarea
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, 280))}
                placeholder={t('bio.placeholder')}
                rows={3}
                maxLength={280}
              />
              <p className="text-xs text-muted-foreground text-right">{bio.length}/280</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <Loader2 className="animate-spin" /> : <Save className="h-4 w-4" />}
              {t('saveChanges')}
            </Button>
          </CardContent>
        </Card>

        {/* Privacy & Visibility */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('privacyAndVisibility')}</CardTitle>
            <p className="text-xs text-muted-foreground">{t('privacyDescription')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('visibility.title')}</Label>
              <div className="space-y-1.5">
                {([
                  { value: "public", label: t('visibility.public'), desc: t('visibilityPublicDesc') },
                  { value: "club_only", label: t('visibilityClubOnly'), desc: t('visibilityClubOnlyDesc') },
                  { value: "private", label: t('visibility.private'), desc: t('visibilityPrivateDesc') },
                ] as const).map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors",
                      profile?.profile_visibility === opt.value
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/30"
                    )}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={opt.value}
                      checked={profile?.profile_visibility === opt.value}
                      onChange={() => updatePrivacy({ profile_visibility: opt.value })}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {profile?.profile_visibility !== "private" && (
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t('showMyLevel')}</p>
                    <p className="text-xs text-muted-foreground">{t('showMyLevelDesc')}</p>
                  </div>
                  <Switch
                    checked={profile?.show_level ?? true}
                    onCheckedChange={(v) => updatePrivacy({ show_level: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t('showMatchHistory')}</p>
                    <p className="text-xs text-muted-foreground">{t('showMatchHistoryDesc')}</p>
                  </div>
                  <Switch
                    checked={profile?.show_match_history ?? true}
                    onCheckedChange={(v) => updatePrivacy({ show_match_history: v })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parent / Children section */}
        <ParentChildrenSection />

        {icalUrl && (
          <Card>
            <CardHeader><CardTitle className="text-lg">{t('syncCalendar')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('syncCalendarDesc')}</p>
              <div className="flex gap-2">
                <Input value={icalUrl} readOnly className="text-xs" />
                <Button variant="outline" size="icon" onClick={copyIcalUrl}>
                  {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm font-medium">Språk / Language</span>
          <LanguagePicker />
        </div>

        <ChangePasswordSection email={user?.email ?? ""} />

        <Button variant="outline" className="w-full text-destructive lg:hidden" disabled={loggingOut} onClick={handleSignOut}>
          {loggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
          {loggingOut ? t('loggingOut') : t('common:logout')}
        </Button>
      </TabsContent>

      <TabsContent value="membership" className="mt-3">
        <MembershipSection />
      </TabsContent>

      <TabsContent value="payments" className="mt-3 space-y-6">
        <SavedCardsSection />
        <PlayerCreditsSection />
        <PaymentHistory />
      </TabsContent>

      <TabsContent value="stats" className="mt-3 space-y-3">
        <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowReportResult(true)}>
          <ClipboardList className="h-3.5 w-3.5" /> {t('reportResult')}
        </Button>
        <PlayerStatsSection />
      </TabsContent>

      <TabsContent value="history" className="mt-3">
        <MatchHistorySection />
      </TabsContent>

      <TabsContent value="friends" className="mt-3 space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate("/players")}>
            <Users className="h-3.5 w-3.5" /> {t('findPlayers')}
          </Button>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate("/messages")}>
            <MessageSquare className="h-3.5 w-3.5" /> {t('messages')}
          </Button>
        </div>
        <FriendsList />
      </TabsContent>

      <TabsContent value="ranking" className="mt-3">
        <RankingList />
      </TabsContent>

      {isEnabled("referral_program") && (
        <TabsContent value="referral" className="mt-3">
          <ReferralSection />
        </TabsContent>
      )}
    </Tabs>
  );

  const desktopSidebar = (
    <div className="lg:sticky lg:top-6 space-y-4">
      {profileHeader}
      <Button variant="outline" className="w-full text-destructive" disabled={loggingOut} onClick={handleSignOut}>
        {loggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
        {loggingOut ? t('loggingOut') : t('common:logout')}
      </Button>
      <ProfileSidebar />
    </div>
  );

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Mobile: profile header shown inline */}
      <div className="lg:hidden flex flex-col items-center gap-3">
        {profileHeader}
      </div>

      <ResponsiveLayout
        main={tabsContent}
        sidebar={desktopSidebar}
        sidebarPosition="left"
        sidebarWidth="w-72"
      />

      <SelfAssessmentModal
        open={showAssessment}
        onOpenChange={setShowAssessment}
        userId={user?.id ?? ""}
        organizationId={organization?.id}
        onComplete={() => window.location.reload()}
      />

      <ReportResultModal open={showReportResult} onOpenChange={setShowReportResult} />
    </div>
  );
}
